import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle, Warning, XCircle, Circle, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import VehicleQrField from "@/components/VehicleQrField";

// Step: 0 pick vehicle & mileage, 1..N items, final submit
export default function InspectionFlow() {
    const [vehicles, setVehicles] = useState([]);
    const [vehicleId, setVehicleId] = useState("");
    const [mileage, setMileage] = useState("");
    const [location, setLocation] = useState("");
    const [gps, setGps] = useState({ lat: null, lng: null });
    const [template, setTemplate] = useState([]);
    const [items, setItems] = useState([]);
    const [step, setStep] = useState(0);
    const [inspectionId, setInspectionId] = useState(null);
    const [overallComment, setOverallComment] = useState("");
    const [sigData, setSigData] = useState("");
    const [starting, setStarting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        api.get("/vehicles", { params: { status: "active" } }).then((r) => setVehicles(r.data));
        api.get("/inspections/template").then((r) => {
            setTemplate(r.data.items);
            setItems(r.data.items.map((it) => ({ key: it.key, label: it.label, status: "pending", comment: "", photo_data_url: "" })));
        });
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude }),
                () => {}
            );
        }
    }, []);

    const beginInspection = async () => {
        if (!vehicleId) {
            toast.error("車両を選択してください");
            return;
        }
        setStarting(true);
        try {
            const { data } = await api.post("/inspections/start", {
                vehicle_id: vehicleId,
                mileage: mileage ? parseInt(mileage) : null,
                location,
                gps_lat: gps.lat,
                gps_lng: gps.lng,
            });
            setInspectionId(data.id);
            setStep(1);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setStarting(false);
        }
    };

    const setItemStatus = (idx, status) => {
        const next = [...items];
        next[idx].status = status;
        setItems(next);
    };
    const setItemComment = (idx, comment) => {
        const next = [...items];
        next[idx].comment = comment;
        setItems(next);
    };
    const setItemPhoto = (idx, data) => {
        const next = [...items];
        next[idx].photo_data_url = data;
        setItems(next);
    };
    const handlePhoto = (idx, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setItemPhoto(idx, ev.target.result);
        reader.readAsDataURL(file);
    };

    // Signature canvas
    useEffect(() => {
        if (step === template.length + 1 && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            let drawing = false;
            const rect = () => canvas.getBoundingClientRect();
            const start = (x, y) => { drawing = true; ctx.beginPath(); ctx.moveTo(x, y); ctx.strokeStyle = "#0F1115"; ctx.lineWidth = 2; };
            const move = (x, y) => { if (!drawing) return; ctx.lineTo(x, y); ctx.stroke(); };
            const end = () => { if (!drawing) return; drawing = false; setSigData(canvas.toDataURL("image/png")); };
            const mdown = (e) => { const r = rect(); start(e.clientX - r.left, e.clientY - r.top); };
            const mmove = (e) => { const r = rect(); move(e.clientX - r.left, e.clientY - r.top); };
            const tstart = (e) => { const r = rect(); const t = e.touches[0]; start(t.clientX - r.left, t.clientY - r.top); e.preventDefault(); };
            const tmove = (e) => { const r = rect(); const t = e.touches[0]; move(t.clientX - r.left, t.clientY - r.top); e.preventDefault(); };
            canvas.addEventListener("mousedown", mdown);
            canvas.addEventListener("mousemove", mmove);
            canvas.addEventListener("mouseup", end);
            canvas.addEventListener("mouseleave", end);
            canvas.addEventListener("touchstart", tstart);
            canvas.addEventListener("touchmove", tmove);
            canvas.addEventListener("touchend", end);
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            return () => {
                canvas.removeEventListener("mousedown", mdown);
                canvas.removeEventListener("mousemove", mmove);
                canvas.removeEventListener("mouseup", end);
                canvas.removeEventListener("mouseleave", end);
                canvas.removeEventListener("touchstart", tstart);
                canvas.removeEventListener("touchmove", tmove);
                canvas.removeEventListener("touchend", end);
            };
        }
    }, [step, template.length]);

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
        setSigData("");
    };

    const submitAll = async () => {
        // validate all statuses set
        const missing = items.findIndex((it) => it.status === "pending");
        if (missing >= 0) {
            toast.error(`項目「${items[missing].label}」の判定が未入力です`);
            setStep(missing + 1);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/inspections/submit", {
                inspection_id: inspectionId,
                items,
                overall_comment: overallComment,
                signature_data_url: sigData,
                mileage: mileage ? parseInt(mileage) : null,
                gps_lat: gps.lat,
                gps_lng: gps.lng,
            });
            setResult(data);
            setStep(template.length + 2);
            toast.success("点検を提出しました");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setStep(0);
        setVehicleId("");
        setMileage("");
        setInspectionId(null);
        setResult(null);
        setOverallComment("");
        setSigData("");
        setItems(template.map((it) => ({ key: it.key, label: it.label, status: "pending", comment: "", photo_data_url: "" })));
    };

    // Screen 0: select vehicle
    if (step === 0) {
        return (
            <div className="max-w-2xl mx-auto" data-testid="inspect-step-0">
                <p className="prc-label-overline">使用前点検</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">ステップ 1/ {template.length + 2} — 車両選択</h1>
                <p className="mt-2 text-sm text-[var(--prc-muted)]">運行前に必須の安全点検を実施します。</p>

                <div className="mt-6 prc-card p-5 space-y-4">
                    <div>
                        <label className="prc-label-overline block mb-1">車両 *</label>
                        <VehicleQrField
                            value={vehicleId}
                            vehicles={vehicles}
                            onPick={(v) => setVehicleId(v.id)}
                            required
                            testid="inspect-vehicle"
                        />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">走行距離 (km)</label>
                        <input type="number" className="prc-input" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="inspect-mileage-input" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">場所（任意）</label>
                        <input className="prc-input" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="inspect-location-input" />
                    </div>
                    {gps.lat && (
                        <p className="text-xs text-[var(--prc-muted)]" data-testid="inspect-gps">GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</p>
                    )}
                </div>

                <button onClick={beginInspection} disabled={starting} className="prc-btn-primary w-full mt-4 inline-flex items-center justify-center gap-2" data-testid="inspect-begin-btn">
                    <ShieldCheck size={24} />{starting ? "開始中..." : "点検を開始する"}
                </button>
            </div>
        );
    }

    // Screens 1..N: item check
    if (step >= 1 && step <= template.length) {
        const idx = step - 1;
        const it = template[idx];
        const cur = items[idx];
        return (
            <div className="max-w-2xl mx-auto" data-testid={`inspect-step-${step}`}>
                <div className="flex justify-between items-center">
                    <div>
                        <p className="prc-label-overline">使用前点検 · ステップ {step + 1}/{template.length + 2}</p>
                        <h1 className="text-2xl font-bold tracking-tight mt-1">{it.label}</h1>
                    </div>
                    <div className="text-right text-xs text-[var(--prc-muted)]">
                        {items.filter((i) => i.status !== "pending").length}/{items.length} 完了
                    </div>
                </div>

                <div className="mt-5 space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { v: "ok", label: "OK", cls: "bg-emerald-50 border-emerald-300 text-emerald-800", Icon: CheckCircle },
                            { v: "warn", label: "要観察", cls: "bg-amber-50 border-amber-300 text-amber-800", Icon: Warning },
                            { v: "ng", label: "NG", cls: "bg-red-50 border-red-300 text-red-800", Icon: XCircle },
                        ].map((opt) => (
                            <button
                                key={opt.v}
                                onClick={() => setItemStatus(idx, opt.v)}
                                className={`p-4 border-2 rounded-sm flex flex-col items-center gap-1 text-sm font-semibold transition ${cur.status === opt.v ? `${opt.cls} border-2` : "bg-[var(--prc-surface-2)] border-[var(--prc-border-strong)] text-[var(--prc-muted)]"}`}
                                data-testid={`inspect-option-${opt.v}`}
                            >
                                <opt.Icon size={24} />
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <div>
                        <label className="prc-label-overline block mb-1">コメント（異常がある場合は必ず記入）</label>
                        <textarea className="prc-input min-h-20" value={cur.comment || ""} onChange={(e) => setItemComment(idx, e.target.value)} data-testid="inspect-comment" />
                    </div>

                    <div>
                        <label className="prc-label-overline block mb-1">写真エビデンス {it.required_photo && <span className="text-[var(--prc-red)]">*必須</span>}</label>
                        {cur.photo_data_url ? (
                            <div>
                                <img src={cur.photo_data_url} alt="evidence" className="w-full max-h-60 object-contain border border-[var(--prc-border)] rounded-sm" data-testid="inspect-photo-preview" />
                                <button onClick={() => setItemPhoto(idx, "")} className="mt-2 text-xs text-[var(--prc-red)]">写真を削除</button>
                            </div>
                        ) : (
                            <label className="prc-btn-ghost w-full justify-center inline-flex items-center gap-2 cursor-pointer" data-testid="inspect-photo-btn">
                                <Camera size={16} /> 写真を撮影／選択
                                <input type="file" accept="image/*" capture="environment" onChange={(e) => handlePhoto(idx, e.target.files?.[0])} className="hidden" />
                            </label>
                        )}
                    </div>
                </div>

                <div className="mt-5 flex justify-between gap-2">
                    <button
                        onClick={() => setStep(step - 1)}
                        disabled={step === 1}
                        className="prc-btn-ghost"
                        data-testid="inspect-prev-btn"
                    >
                        ← 戻る
                    </button>
                    <button
                        onClick={() => {
                            if (it.required_photo && !cur.photo_data_url) {
                                toast.error("写真エビデンスが必須です");
                                return;
                            }
                            if (cur.status === "pending") {
                                toast.error("判定を選択してください");
                                return;
                            }
                            setStep(step + 1);
                        }}
                        className="prc-btn-primary inline-flex items-center gap-2"
                        data-testid="inspect-next-btn"
                    >
                        次へ <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    // Screen N+1: summary & signature
    if (step === template.length + 1) {
        return (
            <div className="max-w-2xl mx-auto" data-testid="inspect-summary">
                <p className="prc-label-overline">使用前点検 · 最終確認</p>
                <h1 className="text-2xl font-bold tracking-tight mt-1">確認と承認</h1>

                <div className="mt-5 prc-card divide-y divide-[var(--prc-border)]">
                    {items.map((it, i) => (
                        <div key={it.key} className="p-4 flex justify-between items-start gap-3">
                            <div>
                                <p className="font-semibold text-sm">{it.label}</p>
                                {it.comment && <p className="text-xs text-[var(--prc-muted)] mt-0.5">{it.comment}</p>}
                            </div>
                            <span className={`prc-badge ${it.status === "ok" ? "ok" : it.status === "warn" ? "warn" : it.status === "ng" ? "ng" : "muted"}`}>
                                {it.status === "pending" ? "未入力" : it.status.toUpperCase()}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="mt-4">
                    <label className="prc-label-overline block mb-1">総合コメント</label>
                    <textarea className="prc-input min-h-20" value={overallComment} onChange={(e) => setOverallComment(e.target.value)} data-testid="inspect-overall-comment" />
                </div>

                <div className="mt-4">
                    <label className="prc-label-overline block mb-1">署名</label>
                    <canvas ref={canvasRef} className="prc-signature" data-testid="inspect-signature-canvas" />
                    <button onClick={clearSignature} className="mt-2 text-xs text-[var(--prc-muted)]" data-testid="inspect-sig-clear">署名をクリア</button>
                </div>

                <div className="mt-5 flex justify-between">
                    <button onClick={() => setStep(step - 1)} className="prc-btn-ghost" data-testid="inspect-back-summary">← 戻る</button>
                    <button onClick={submitAll} disabled={submitting} className="prc-btn-primary inline-flex items-center gap-2" data-testid="inspect-submit-btn">
                        <ShieldCheck size={16} />{submitting ? "送信中…" : "点検を確定・提出"}
                    </button>
                </div>
            </div>
        );
    }

    // Result
    if (step === template.length + 2 && result) {
        const ok = result.approved;
        return (
            <div className="max-w-xl mx-auto text-center" data-testid="inspect-result">
                <div className={`prc-card p-10 ${ok ? "" : "border-[var(--prc-red)]"}`}>
                    {ok ? <CheckCircle size={24} className="mx-auto text-[var(--prc-green)]" weight="fill" /> : <XCircle size={24} className="mx-auto text-[var(--prc-red)]" weight="fill" />}
                    <h2 className="mt-4 text-2xl font-bold tracking-tight">{ok ? "点検完了・運行許可" : "運行不可（NG項目あり）"}</h2>
                    <p className="mt-2 text-sm text-[var(--prc-muted)]">
                        {ok
                            ? "24時間以内は本車両を運行できます。"
                            : "整備責任者に即時連絡してください。NG項目の修復後に再点検が必要です。"}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs text-[var(--prc-muted)]">
                        結果: <span className={`prc-badge ${ok ? "ok" : "ng"}`}>{result.overall_status?.toUpperCase()}</span>
                    </div>
                </div>
                <button onClick={reset} className="prc-btn-primary mt-6" data-testid="inspect-reset-btn">新しい点検を開始</button>
            </div>
        );
    }

    return null;
}
